import { LegalEntityParamFilter } from '../../entities/legal-entities/legal-entity-param-filter';
import { ManagerUrlParamsFilter } from '../../entities/filters/manager-url-params-filter';
import { DateUrlParamsFilter } from '../../entities/filters/date-url-params-filter';
import { useOurCompany } from '../../hooks/portal-settings.hook';
import { createRangeSummaryFormatter, FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useSearchParams } from 'react-router-dom';
import { useLegalEntityLabel } from 'shared/hooks/use-entity-labels';
import { Stack } from '@mui/material';
import { CONTRACT_TYPE_CLIENT, ContractType } from '@sr/dto';

type Props = {
  companyId: number;
  contractType: ContractType;
  queryParamPrefix: string;
};

export const CompanyContractsFilter = ({ companyId, contractType, queryParamPrefix }: Props) => {
  const ourCompanyId = useOurCompany();
  const { items: managers } = useManagersDropdownList();
  const [searchParams] = useSearchParams();
  const isClient = contractType === CONTRACT_TYPE_CLIENT;
  const sellerParamName = `${queryParamPrefix}sellerLegalEntityId`;
  const buyerParamName = `${queryParamPrefix}buyerLegalEntityId`;
  const managerParamName = `${queryParamPrefix}managerId`;
  const fromDateParamName = `${queryParamPrefix}fromDate`;
  const toDateParamName = `${queryParamPrefix}toDate`;
  const createdAtFromParamName = `${queryParamPrefix}createdAtFrom`;
  const createdAtToParamName = `${queryParamPrefix}createdAtTo`;

  const sellerLegalEntityId = searchParams.get(sellerParamName);
  const buyerLegalEntityId = searchParams.get(buyerParamName);
  const sellerLabel = useLegalEntityLabel(sellerLegalEntityId);
  const buyerLabel = useLegalEntityLabel(buyerLegalEntityId);

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const sellerFormatter = ({ value }: ValueFormatterContext) => {
    if (sellerLabel && sellerLegalEntityId === value) return sellerLabel;
    return value;
  };

  const buyerFormatter = ({ value }: ValueFormatterContext) => {
    if (buyerLabel && buyerLegalEntityId === value) return buyerLabel;
    return value;
  };

  return (
    <FilterWrapper title="Фильтры договоров">
      <FilterWrapper.Item label="Юрлицо продавца" queryKeys={sellerParamName} valueFormatter={sellerFormatter}>
        <LegalEntityParamFilter queryParamName={sellerParamName} companyId={isClient ? ourCompanyId : companyId} label="Юрлицо продавца" />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Юрлицо покупателя" queryKeys={buyerParamName} valueFormatter={buyerFormatter}>
        <LegalEntityParamFilter queryParamName={buyerParamName} companyId={isClient ? companyId : ourCompanyId} label="Юрлицо покупателя" />
      </FilterWrapper.Item>

      {isClient && (
        <FilterWrapper.Item label="Менеджер" queryKeys={managerParamName} valueFormatter={managerValueFormatter}>
          <ManagerUrlParamsFilter queryParamName={managerParamName} withNonSelect={true} />
        </FilterWrapper.Item>
      )}

      <FilterWrapper.Item
        label="Дата окончания"
        queryKeys={[fromDateParamName, toDateParamName]}
        summaryFormatter={createRangeSummaryFormatter({ fromKey: fromDateParamName, toKey: toDateParamName, emptyLabel: 'Дата окончания', activeLabel: 'Окончание' })}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={fromDateParamName} label="Дата окончания с" />
          <DateUrlParamsFilter queryParamName={toDateParamName} label="Дата окончания до" />
        </Stack>
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Дата создания"
        queryKeys={[createdAtFromParamName, createdAtToParamName]}
        summaryFormatter={createRangeSummaryFormatter({ fromKey: createdAtFromParamName, toKey: createdAtToParamName, emptyLabel: 'Дата создания', activeLabel: 'Создание' })}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName={createdAtFromParamName} label="Дата создания с" />
          <DateUrlParamsFilter queryParamName={createdAtToParamName} label="Дата создания по" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
